import { useEffect, useRef, useState } from "react"
import { Icon, Reveal, TextLink } from "./shared"

/* ----------------------------------------------------------------------------
   The site's living parts: cards, step flows, counters and small pictures of
   the systems we build.

   The pictures are markup rather than photos, so they stay sharp and show the
   work itself. They are decorative and illustrative: keep client data and
   performance figures out of them. Motion lives in index.css keyframes and
   stops for visitors who ask for reduced motion, and everything reads
   correctly with no JavaScript and no animation at all.
---------------------------------------------------------------------------- */

const CARD_TONES = {
  ink: {
    card: "bg-ink text-white",
    muted: "text-neutral-400",
    number: "text-accent-bright",
    tag: "border-white/15 text-neutral-300",
    light: true,
  },
  glow: {
    card: "bg-ink bg-[radial-gradient(80%_100%_at_100%_0%,rgba(23,76,138,0.6)_0%,transparent_70%)] text-white",
    muted: "text-neutral-400",
    number: "text-accent-bright",
    tag: "border-white/15 text-neutral-300",
    light: true,
  },
  accent: {
    card: "bg-accent text-white [&_a]:text-white",
    muted: "text-white/80",
    number: "text-white/70",
    tag: "border-white/30 text-white",
    light: true,
  },
  mist: {
    card: "bg-mist text-ink",
    muted: "text-neutral-600",
    number: "text-accent",
    tag: "border-neutral-300 text-neutral-700",
    light: false,
  },
  white: {
    card: "border border-neutral-200 bg-white text-ink",
    muted: "text-neutral-600",
    number: "text-accent",
    tag: "border-neutral-300 text-neutral-700",
    light: false,
  },
  /** For a card sitting on an ink section. */
  smoke: {
    card: "border border-white/10 bg-white/[0.04] text-white",
    muted: "text-neutral-400",
    number: "text-accent-bright",
    tag: "border-white/15 text-neutral-300",
    light: true,
  },
} as const

export type CardTone = keyof typeof CARD_TONES

/** Set `flush` when the visual is a window cut off by the card's bottom edge. */
export function BentoCard({
  tone,
  n,
  meta,
  title,
  text,
  tags,
  link,
  visual,
  flush = false,
}: {
  tone: CardTone
  n?: string
  meta?: string
  title: string
  text: string
  tags?: readonly string[]
  link?: { to: string; label: string }
  visual: React.ReactNode
  flush?: boolean
}) {
  const c = CARD_TONES[tone]
  return (
    <article
      className={`group flex h-full flex-col overflow-hidden rounded-3xl p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_34px_60px_-34px_rgba(11,16,23,0.55)] lg:p-9 ${c.card}`}
    >
      {(n || meta) && (
        <div className="mb-5 flex items-baseline justify-between gap-6">
          <span className={`text-[13px] font-black tracking-widest ${c.number}`}>
            {n}
          </span>
          <span className={`text-right text-[12.5px] font-medium ${c.muted}`}>
            {meta}
          </span>
        </div>
      )}
      <h3 className="max-w-lg text-[clamp(1.45rem,2.2vw,1.95rem)] font-black leading-[1.12] tracking-[-0.025em]">
        {title}
      </h3>
      <p className={`mt-4 max-w-lg text-[15.5px] leading-[1.65] ${c.muted}`}>
        {text}
      </p>
      {tags && (
        <ul className="mt-5 flex flex-wrap gap-2">
          {tags.map((t) => (
            <li
              key={t}
              className={`rounded-full border px-3 py-1 text-[12.5px] ${c.tag}`}
            >
              {t}
            </li>
          ))}
        </ul>
      )}
      {link && (
        <div className="mt-6">
          <TextLink to={link.to} light={c.light}>
            {link.label}
          </TextLink>
        </div>
      )}
      <div
        aria-hidden="true"
        className={`mt-auto pt-9 ${flush ? "-mb-7 lg:-mb-9" : ""}`}
      >
        {visual}
      </div>
    </article>
  )
}

/**
 * Connected stages with a pulse that runs through them in order, one rail at a
 * time. There must be a matching relay-<count> keyframe in index.css.
 */
export function StepFlow({
  steps,
  className = "md:grid-cols-3",
}: {
  steps: readonly { n: string; t: string; d: string }[]
  className?: string
}) {
  const count = steps.length
  const beat = 1.6
  return (
    <div className={`grid grid-cols-1 gap-5 ${className}`}>
      {steps.map((s, i) => (
        <Reveal key={s.n} delay={(i % 3) * 80}>
          <div className="group h-full rounded-2xl border border-neutral-200 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-transparent hover:bg-ink hover:shadow-[0_24px_50px_-30px_rgba(11,16,23,0.5)]">
            <div className="flex items-center gap-4">
              <span className="text-[13px] font-black tracking-widest text-accent transition-colors group-hover:text-accent-bright">
                {s.n}
              </span>
              <span
                aria-hidden="true"
                className="relative h-0.5 flex-1 bg-neutral-200 transition-colors group-hover:bg-white/20"
              >
                <span
                  className="live-relay absolute -top-[3px] h-2 w-2 rounded-full bg-accent-bright opacity-0 shadow-[0_0_10px_2px_rgba(56,182,255,0.6)]"
                  style={{
                    animation: `relay-${count} ${count * beat}s linear ${i * beat}s infinite`,
                  }}
                />
              </span>
            </div>
            <h3 className="mt-5 text-[20px] font-bold leading-snug tracking-[-0.02em] transition-colors group-hover:text-white">
              {s.t}
            </h3>
            <p className="mt-3 text-[15px] leading-[1.7] text-neutral-600 transition-colors group-hover:text-neutral-300">
              {s.d}
            </p>
          </div>
        </Reveal>
      ))}
    </div>
  )
}

/**
 * Counts a figure up when it scrolls into view. The server and the first client
 * render both print the final value, so crawlers and visitors without
 * JavaScript get the real number, and anything that isn't a figure ("Indigenous
 * Energy") is left as written.
 */
export function CountUp({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el = ref.current
    const match = value.match(/^([+\-−~]?)(\d[\d,]*(?:\.\d+)?)(.*)$/s)
    if (!el || !match) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    const [, sign, digits, rest] = match
    const target = Number(digits!.replace(/,/g, ""))
    const decimals = digits!.split(".")[1]?.length ?? 0
    let frame = 0

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        io.disconnect()
        const start = performance.now()
        const tick = (now: number) => {
          const t = Math.min((now - start) / 1200, 1)
          const eased = 1 - Math.pow(1 - t, 3)
          el.textContent = `${sign}${(target * eased).toFixed(decimals)}${rest}`
          if (t < 1) frame = requestAnimationFrame(tick)
          else el.textContent = value
        }
        frame = requestAnimationFrame(tick)
      },
      { threshold: 0.4 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      cancelAnimationFrame(frame)
      el.textContent = value
    }
  }, [value])

  return <span ref={ref}>{value}</span>
}

/* A line with a dot travelling along it. Sits inside a `relative` parent. */
function FlowLine({ className, duration = "3.6s" }: { className: string; duration?: string }) {
  return (
    <div className={`absolute h-0.5 ${className}`}>
      <span
        className="absolute -top-[3px] h-2 w-2 animate-[flow_3.6s_linear_infinite] rounded-full bg-white shadow-[0_0_12px_3px_rgba(255,255,255,0.6)] motion-reduce:hidden"
        style={{ animationDuration: duration }}
      />
    </div>
  )
}

/* ---- Services ------------------------------------------------------------ */

export function SoftwareVisual() {
  const rows = [
    { l: "Order", s: "Paid", c: "bg-emerald-400/15 text-emerald-300" },
    { l: "Release", s: "Approved", c: "bg-sky-400/15 text-sky-300" },
    { l: "Ticket", s: "Printed", c: "bg-white/10 text-neutral-200" },
    { l: "Exit", s: "Logged", c: "bg-emerald-400/15 text-emerald-300" },
  ]
  return (
    <div className="overflow-hidden rounded-t-xl border border-b-0 border-white/15 bg-white/[0.05]">
      <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-3">
        <span className="h-2 w-2 rounded-full bg-white/25" />
        <span className="h-2 w-2 rounded-full bg-white/25" />
        <span className="h-2 w-2 rounded-full bg-white/25" />
        <span className="ml-3 text-[11px] font-semibold text-neutral-400">
          Operations
        </span>
      </div>
      <div className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-5">
        <div className="flex h-28 items-end gap-2 sm:col-span-2 sm:h-auto">
          {[45, 70, 55, 90, 65, 100].map((h, i) => (
            <span
              key={i}
              style={{ height: `${h}%`, animationDelay: `${i * 0.35}s` }}
              className="flex-1 origin-bottom animate-[rise_3.2s_ease-in-out_infinite_alternate] rounded-t bg-accent-bright/70 even:bg-accent-bright/35"
            />
          ))}
        </div>
        <ul className="space-y-2 sm:col-span-3">
          {rows.map((r) => (
            <li
              key={r.l}
              className="flex items-center justify-between rounded-lg bg-white/[0.05] px-3 py-2 text-[12px] font-medium text-neutral-200"
            >
              {r.l}
              <span className={`rounded-full px-2 py-0.5 text-[10.5px] ${r.c}`}>
                {r.s}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export function WorkflowVisual() {
  const steps = [
    { l: "Request", i: "plus" as const },
    { l: "Approve", i: "check" as const },
    { l: "Notify", i: "phone" as const },
    { l: "Report", i: "growth" as const },
  ]
  return (
    <div className="relative rounded-xl bg-white/10 px-4 py-7">
      <div className="absolute inset-x-[12.5%] top-[3.25rem] h-0.5 bg-white/25" />
      <FlowLine className="inset-x-[12.5%] top-[3.25rem]" />
      <ol className="relative grid grid-cols-4">
        {steps.map((s) => (
          <li key={s.l} className="flex flex-col items-center gap-2.5">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-white text-accent shadow-lg">
              <Icon name={s.i} className="h-5 w-5" />
            </span>
            <span className="text-[11.5px] font-semibold text-white">{s.l}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

function Ping({ className = "bg-emerald-400" }: { className?: string }) {
  return (
    <span className="relative flex h-2 w-2 shrink-0">
      <span
        className={`absolute h-full w-full animate-ping rounded-full opacity-60 motion-reduce:hidden ${className}`}
      />
      <span className={`relative h-2 w-2 rounded-full ${className}`} />
    </span>
  )
}

export function TrackerVisual() {
  const steps = [
    { l: "Application received", done: true },
    { l: "Documents verified", done: true },
    { l: "Under review", done: false },
    { l: "Certificate issued", done: false },
  ]
  const current = steps.findIndex((s) => !s.done)
  return (
    <ol className="rounded-xl border border-neutral-200 bg-white p-5 shadow-[0_24px_40px_-32px_rgba(11,16,23,0.4)]">
      {steps.map((s, i) => (
        <li key={s.l} className="relative flex items-center gap-3 pb-4 last:pb-0">
          {i < steps.length - 1 && (
            <span
              className={`absolute left-[9px] top-5 h-full w-0.5 ${s.done ? "bg-accent" : "bg-neutral-200"}`}
            />
          )}
          <span
            className={`relative grid h-5 w-5 shrink-0 place-items-center rounded-full ${
              s.done
                ? "bg-accent text-white"
                : i === current
                  ? "bg-accent/15"
                  : "bg-neutral-100"
            }`}
          >
            {s.done && <Icon name="check" className="h-3 w-3" />}
            {i === current && (
              <>
                <span className="absolute h-5 w-5 animate-ping rounded-full bg-accent/30 motion-reduce:hidden" />
                <span className="h-2 w-2 rounded-full bg-accent" />
              </>
            )}
          </span>
          <span
            className={`text-[13px] font-semibold ${
              s.done || i === current ? "text-ink" : "text-neutral-400"
            }`}
          >
            {s.l}
          </span>
        </li>
      ))}
    </ol>
  )
}

export function MonitorVisual() {
  const checks = ["Application", "Database", "Backups", "Security updates"]
  return (
    <div className="grid grid-cols-1 gap-5 overflow-hidden rounded-t-xl border border-b-0 border-white/15 bg-white/[0.05] p-5 sm:grid-cols-2">
      <ul className="space-y-2.5">
        {checks.map((c) => (
          <li
            key={c}
            className="flex items-center gap-2.5 text-[12.5px] font-medium text-neutral-200"
          >
            <Ping />
            {c}
          </li>
        ))}
      </ul>
      <svg
        viewBox="0 0 240 90"
        preserveAspectRatio="none"
        className="h-24 w-full text-accent-bright sm:h-full"
      >
        <polyline
          points="0,62 30,50 60,58 90,34 120,44 150,24 180,36 210,16 240,26"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          vectorEffect="non-scaling-stroke"
          pathLength={1}
          strokeDasharray="1"
          className="animate-[draw_2.4s_ease-in-out_infinite_alternate]"
        />
      </svg>
    </div>
  )
}

/* ---- Government ---------------------------------------------------------- */

export function PortalVisual() {
  return (
    <div className="rounded-xl bg-white p-5 text-ink shadow-[0_24px_40px_-28px_rgba(11,16,23,0.6)]">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] font-bold">Apply online</span>
        <span className="flex items-center gap-2 rounded-full bg-mist px-2.5 py-1 text-[11px] font-semibold text-neutral-700">
          <Ping className="bg-accent" />
          Submitted
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {["Full name", "Service", "Location", "Documents"].map((f) => (
          <div
            key={f}
            className="rounded-lg border border-neutral-200 px-3 py-2 text-[11.5px] text-neutral-500"
          >
            {f}
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg bg-ink py-2 text-center text-[12px] font-semibold text-white">
        Track my application
      </div>
    </div>
  )
}

export function RecordsVisual() {
  const history = [
    { l: "Record created", w: "w-20" },
    { l: "Details updated", w: "w-14" },
    { l: "Approved by officer", w: "w-16" },
  ]
  return (
    <div className="rounded-xl border border-white/15 bg-white/[0.05] p-4">
      <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.07] px-3 py-2 text-[12px] text-neutral-400">
        <span className="h-2.5 w-2.5 rounded-full border-2 border-accent-bright" />
        Search records
      </div>
      <ul className="mt-4 space-y-3">
        {history.map((h, i) => (
          <li
            key={h.l}
            className="flex items-center justify-between gap-3 text-[12px] font-medium text-neutral-200"
          >
            <span className="flex items-center gap-2.5">
              {i === history.length - 1 ? (
                <Ping className="bg-accent-bright" />
              ) : (
                <span className="h-2 w-2 rounded-full bg-white/30" />
              )}
              {h.l}
            </span>
            <span className={`h-1.5 rounded-full bg-white/15 ${h.w}`} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export function ReconcileVisual() {
  return (
    <div className="rounded-xl border border-white/15 bg-white/[0.05] p-5">
      <div className="grid grid-cols-[1fr_3rem_1fr] gap-y-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-400 sm:grid-cols-[1fr_6rem_1fr]">
        <span>Payments</span>
        <span />
        <span>Bank lines</span>
        {[0, 1, 2].map((i) => (
          <div key={i} className="contents">
            <span className="h-8 rounded-lg bg-white/[0.08]" />
            <span className="relative self-center">
              <span className="block h-0.5 bg-white/20" />
              <FlowLine
                className="inset-x-0 top-0"
                duration={`${3 + i * 0.7}s`}
              />
            </span>
            <span className="flex h-8 items-center justify-end rounded-lg bg-white/[0.08] pr-2 text-emerald-300">
              <Icon name="check" className="h-3.5 w-3.5" />
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function SitesVisual() {
  const sites = [
    { x: 60, y: 30 },
    { x: 60, y: 110 },
    { x: 180, y: 12 },
    { x: 300, y: 30 },
    { x: 300, y: 110 },
  ]
  return (
    <svg viewBox="0 0 360 140" className="h-auto w-full">
      {sites.map((s, i) => {
        const d = `M180 72 L${s.x} ${s.y}`
        return (
          <g key={i}>
            <path d={d} stroke="rgba(255,255,255,.18)" strokeWidth="1.5" fill="none" />
            <path
              d={d}
              pathLength={100}
              strokeDasharray="12 88"
              stroke="#38b6ff"
              strokeWidth="2"
              fill="none"
              className="animate-[signal_3.4s_linear_infinite]"
              style={{ animationDelay: `${i * 0.6}s` }}
            />
            <circle cx={s.x} cy={s.y} r="9" fill="#18212d" stroke="rgba(255,255,255,.35)" />
          </g>
        )
      })}
      <circle cx="180" cy="72" r="24" fill="#0b66c3" />
      <text
        x="180"
        y="76"
        textAnchor="middle"
        fontSize="11"
        fontWeight="700"
        fill="#fff"
      >
        HQ
      </text>
    </svg>
  )
}

export function IntegrateVisual() {
  const boxes = ["Existing system", "API", "New platform"]
  return (
    <div className="relative rounded-xl bg-white/10 p-4">
      <div className="absolute inset-x-[16%] top-1/2 h-0.5 bg-white/25" />
      <FlowLine className="inset-x-[16%] top-1/2" />
      <ul className="relative grid grid-cols-3 items-center gap-3">
        {boxes.map((b, i) => (
          <li
            key={b}
            className={`rounded-lg px-1.5 py-3 text-center text-[11.5px] font-semibold ${
              i === 1 ? "bg-ink text-white" : "bg-white text-accent"
            }`}
          >
            {b}
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ---- Principles ---------------------------------------------------------- */

export function GoalVisual() {
  return (
    <div className="rounded-xl bg-white/10 p-5">
      <div className="relative flex h-24 items-end gap-2.5">
        <div className="absolute inset-x-0 top-2 border-t-2 border-dashed border-white/60" />
        <span className="absolute right-0 top-3.5 text-[10.5px] font-semibold text-white/90">
          Agreed result
        </span>
        {[28, 44, 58, 76].map((h, i) => (
          <span
            key={h}
            style={{ height: `${h}%`, animationDelay: `${i * 0.3}s` }}
            className="w-8 origin-bottom animate-[rise_3s_ease-in-out_infinite_alternate] rounded-t bg-white/80"
          />
        ))}
      </div>
    </div>
  )
}

/* One highlight stepping through a row of options. Needs a spot-<count> keyframe. */
function SpotOptions({
  options,
  base,
  spot,
}: {
  options: readonly string[]
  base: string
  spot: string
}) {
  const beat = 2
  const count = options.length
  return (
    <ul className="grid grid-cols-3 gap-2">
      {options.map((o, i) => (
        <li
          key={o}
          className={`relative overflow-hidden rounded-lg px-1.5 py-2.5 text-center text-[11.5px] font-semibold ${base}`}
        >
          <span
            className={`live-relay absolute inset-0 opacity-0 ${spot}`}
            style={{
              animation: `spot-${count} ${count * beat}s linear ${i * beat}s infinite`,
            }}
          />
          <span className="relative">{o}</span>
        </li>
      ))}
    </ul>
  )
}

export function InterfaceVisual() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-mist p-4">
      <SpotOptions
        options={["Finance", "Security", "Management"]}
        base="bg-white text-neutral-700"
        spot="bg-accent/20"
      />
      <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-3 text-[12px] font-semibold text-ink">
        What this person needs next
        <span className="rounded-md bg-ink px-2.5 py-1 text-[10.5px] text-white">
          Open
        </span>
      </div>
    </div>
  )
}

export function ToolVisual() {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.05] p-4">
      <p className="mb-3 text-[12px] font-medium text-neutral-400">
        What does this problem actually need?
      </p>
      <SpotOptions
        options={["Plain software", "Automation", "AI"]}
        base="bg-white/[0.06] text-neutral-200"
        spot="bg-accent/70"
      />
    </div>
  )
}

export function GrowVisual() {
  return (
    <ul className="grid grid-cols-2 gap-2.5 text-[12px] font-semibold">
      {["Orders", "Finance", "Reporting"].map((m) => (
        <li key={m} className="rounded-lg bg-ink px-3 py-3 text-white">
          {m}
        </li>
      ))}
      <li className="flex animate-pulse items-center gap-1.5 rounded-lg border-2 border-dashed border-accent/50 px-3 py-2.5 text-accent motion-reduce:animate-none">
        <Icon name="plus" className="h-3.5 w-3.5" />
        Next module
      </li>
    </ul>
  )
}

/** The four principles, in order, wherever they appear. */
export const PRINCIPLE_CARDS = [
  { Visual: GoalVisual, span: "lg:col-span-5", tone: "accent" },
  { Visual: InterfaceVisual, span: "lg:col-span-7", tone: "white" },
  { Visual: ToolVisual, span: "lg:col-span-7", tone: "ink" },
  { Visual: GrowVisual, span: "lg:col-span-5", tone: "white" },
] as const

/* ---- Working with us ----------------------------------------------------- */

export function StatusVisual() {
  const rows = [
    { l: "Scope agreed", done: true },
    { l: "Build in progress", done: false },
    { l: "Next update scheduled", done: false },
  ]
  return (
    <div className="rounded-xl border border-white/15 bg-white/[0.05] p-4">
      <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
        <span className="block h-full w-3/5 origin-left animate-[rise-x_3s_ease-in-out_infinite_alternate] rounded-full bg-accent-bright" />
      </div>
      <ul className="mt-4 space-y-2.5 text-[12.5px] font-medium text-neutral-200">
        {rows.map((r, i) => (
          <li key={r.l} className="flex items-center gap-2.5">
            {r.done ? (
              <Icon name="check" className="h-3.5 w-3.5 text-emerald-300" />
            ) : i === 1 ? (
              <Ping className="bg-accent-bright" />
            ) : (
              <span className="h-2 w-2 rounded-full bg-white/25" />
            )}
            {r.l}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function TalkVisual() {
  return (
    <div className="relative flex items-center justify-between rounded-xl bg-white/10 px-5 py-6 text-[11.5px] font-semibold text-white">
      <div className="absolute inset-x-16 top-[2.65rem] h-0.5 bg-white/25" />
      <FlowLine className="inset-x-16 top-[2.65rem]" />
      {["You", "Engineer"].map((p) => (
        <span key={p} className="relative flex w-14 flex-col items-center gap-2">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-white text-[14px] font-black text-accent">
            {p[0]}
          </span>
          {p}
        </span>
      ))}
    </div>
  )
}

export function LaunchVisual() {
  const checks = ["Tested with real users", "Team trained", "Ready to go live"]
  return (
    <ul className="space-y-2.5 rounded-xl bg-mist p-4 text-[12.5px] font-semibold text-ink">
      {checks.map((c, i) => (
        <li key={c} className="flex items-center gap-2.5">
          {i < checks.length - 1 ? (
            <Icon name="check" className="h-3.5 w-3.5 text-accent" />
          ) : (
            <Ping className="bg-accent" />
          )}
          {c}
        </li>
      ))}
    </ul>
  )
}

/* ---- Home hero ----------------------------------------------------------- */

/**
 * A system at work: nodes drifting, linking up when they come close, with
 * packets of data hopping between them. The cursor stirs the nodes nearby.
 *
 * Canvas rather than DOM because this is hundreds of moving lines. It only runs
 * while the hero is on screen and the tab is visible, and draws a single still
 * frame for visitors who ask for reduced motion. The server renders an empty
 * canvas, so the section behind it has to look fine alone.
 */
export function HeroNetwork() {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) return

    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const LINK = 150
    const REACH = 180
    type Node = { x: number; y: number; vx: number; vy: number }
    type Packet = { from: number; to: number; t: number }
    let nodes: Node[] = []
    let packets: Packet[] = []
    let w = 0
    let h = 0
    let frame = 0
    let running = false
    const pointer = { x: -9999, y: -9999 }

    const neighbours = (i: number) => {
      const out: number[] = []
      nodes.forEach((n, j) => {
        if (j !== i && Math.hypot(n.x - nodes[i]!.x, n.y - nodes[i]!.y) < LINK) out.push(j)
      })
      return out
    }

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      w = rect.width
      h = rect.height
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      const count = Math.min(Math.round((w * h) / 11000), 120)
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
      }))
      packets = []
      if (still || !running) draw()
    }

    const draw = () => {
      ctx.clearRect(0, 0, w, h)
      ctx.lineWidth = 1
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]!
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]!
          const d = Math.hypot(a.x - b.x, a.y - b.y)
          if (d < LINK) {
            ctx.strokeStyle = `rgba(90,169,255,${0.55 * (1 - d / LINK)})`
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.stroke()
          }
        }
        const near = Math.hypot(a.x - pointer.x, a.y - pointer.y) < REACH
        ctx.fillStyle = near ? "#ffffff" : "rgba(170,212,255,0.9)"
        ctx.beginPath()
        ctx.arc(a.x, a.y, near ? 2.6 : 1.7, 0, Math.PI * 2)
        ctx.fill()
      }
      for (const p of packets) {
        const a = nodes[p.from]!
        const b = nodes[p.to]!
        const x = a.x + (b.x - a.x) * p.t
        const y = a.y + (b.y - a.y) * p.t
        ctx.fillStyle = "rgba(56,182,255,0.35)"
        ctx.beginPath()
        ctx.arc(x, y, 7, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = "#ffffff"
        ctx.beginPath()
        ctx.arc(x, y, 2.4, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    const step = () => {
      for (const n of nodes) {
        const dx = n.x - pointer.x
        const dy = n.y - pointer.y
        const d = Math.hypot(dx, dy)
        if (d < REACH && d > 0) {
          const push = ((REACH - d) / REACH) * 0.35
          n.x += (dx / d) * push
          n.y += (dy / d) * push
        }
        n.x += n.vx
        n.y += n.vy
        if (n.x < 0 || n.x > w) n.vx *= -1
        if (n.y < 0 || n.y > h) n.vy *= -1
      }
      if (packets.length < Math.ceil(nodes.length / 6) && Math.random() < 0.08) {
        const from = Math.floor(Math.random() * nodes.length)
        const options = neighbours(from)
        const to = options[Math.floor(Math.random() * options.length)]
        if (to !== undefined) packets.push({ from, to, t: 0 })
      }
      /* A packet that arrives hops on to a neighbour, so data visibly travels
         through the system rather than blinking between two points. */
      packets = packets.flatMap((p) => {
        p.t += 0.018
        if (p.t < 1) return [p]
        const options = neighbours(p.to).filter((n) => n !== p.from)
        const next = options[Math.floor(Math.random() * options.length)]
        return next === undefined ? [] : [{ from: p.to, to: next, t: 0 }]
      })
    }

    const loop = () => {
      step()
      draw()
      frame = requestAnimationFrame(loop)
    }
    const start = () => {
      if (running || still) return
      running = true
      frame = requestAnimationFrame(loop)
    }
    const stop = () => {
      running = false
      cancelAnimationFrame(frame)
    }

    resize()
    const sizes = new ResizeObserver(resize)
    sizes.observe(canvas)

    const seen = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !document.hidden) start()
      else stop()
    })
    seen.observe(canvas)

    const onVisibility = () => {
      if (document.hidden) stop()
      else if (canvas.getBoundingClientRect().bottom > 0) start()
    }
    const onPointer = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      pointer.x = e.clientX - rect.left
      pointer.y = e.clientY - rect.top
    }
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("pointermove", onPointer, { passive: true })

    return () => {
      stop()
      sizes.disconnect()
      seen.disconnect()
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("pointermove", onPointer)
    }
  }, [])

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  )
}

/**
 * Cycles one word in the headline. Decorative: the h1 carries the full sentence
 * for screen readers and crawlers, so this stays out of the accessibility tree.
 */
export function WordCycle({ words }: { words: readonly string[] }) {
  const [i, setI] = useState(0)

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const timer = setInterval(() => setI((n) => (n + 1) % words.length), 2600)
    return () => clearInterval(timer)
  }, [words.length])

  return (
    <span
      key={words[i]}
      className="block animate-[word-in_0.6s_cubic-bezier(0.22,1,0.36,1)] bg-gradient-to-r sm:inline-block from-accent-bright to-white bg-clip-text pb-[0.1em] text-transparent"
    >
      {words[i]}
    </span>
  )
}

const CONSOLE_SCENES = [
  {
    tab: "Operations",
    caption: "Custom software and enterprise applications",
    visual: <SoftwareVisual />,
  },
  {
    tab: "Automation",
    caption: "Digital transformation and process automation",
    visual: <WorkflowVisual />,
  },
  {
    tab: "Public service",
    caption: "Government and institutional technology",
    visual: <TrackerVisual />,
  },
  {
    tab: "Support",
    caption: "Software maintenance, integration and support",
    visual: <MonitorVisual />,
  },
]

const SCENE_MS = 5000

/** The four things we build, playing in turn. Hovering or focusing it holds the
    current scene, and picking a tab stops the rotation for good. */
export function HeroConsole() {
  const [active, setActive] = useState(0)
  const [held, setHeld] = useState(false)
  const [picked, setPicked] = useState(false)
  const [moving, setMoving] = useState(false)

  useEffect(() => {
    setMoving(!window.matchMedia("(prefers-reduced-motion: reduce)").matches)
  }, [])

  const playing = moving && !held && !picked

  useEffect(() => {
    if (!playing) return
    const timer = setTimeout(
      () => setActive((n) => (n + 1) % CONSOLE_SCENES.length),
      SCENE_MS,
    )
    return () => clearTimeout(timer)
  }, [playing, active])

  const scene = CONSOLE_SCENES[active]!

  return (
    <div
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
      className="rounded-3xl border border-white/15 bg-ink/70 p-3 shadow-[0_50px_100px_-40px_rgba(0,0,0,0.95)] backdrop-blur-md"
    >
      <div className="grid grid-cols-4 gap-1" role="group" aria-label="What we build">
        {CONSOLE_SCENES.map((s, i) => (
          <button
            key={s.tab}
            type="button"
            aria-pressed={i === active}
            onClick={() => {
              setActive(i)
              setPicked(true)
            }}
            className={`relative overflow-hidden rounded-xl px-1 py-2.5 text-[11.5px] font-semibold leading-tight transition-colors sm:text-[12.5px] ${
              i === active
                ? "bg-white/10 text-white"
                : "text-neutral-400 hover:bg-white/5 hover:text-white"
            }`}
          >
            {s.tab}
            {i === active && (
              <span
                aria-hidden="true"
                className={`absolute inset-x-0 bottom-0 h-0.5 origin-left bg-accent-bright ${
                  playing ? "animate-[tab-fill_5s_linear]" : ""
                }`}
              />
            )}
          </button>
        ))}
      </div>
      {/* The fixed height stops the page jumping as scenes of different sizes swap. */}
      <div className="flex min-h-[330px] flex-col justify-center px-3 py-5 sm:min-h-[290px]">
        <div
          key={scene.tab}
          aria-hidden="true"
          className="animate-[scene-in_0.55s_cubic-bezier(0.22,1,0.36,1)] [&>*]:rounded-xl [&>div]:border-b"
        >
          {scene.visual}
        </div>
      </div>
      <p className="border-t border-white/10 px-3 pb-2 pt-4 text-[13px] font-medium text-neutral-300">
        {scene.caption}
      </p>
    </div>
  )
}
